import React, { useState, useCallback, useEffect, useRef } from 'react';
import { colors } from '../../base/ui/Tokens';
import { twilioVoiceService } from '../services/TwilioVoiceService';
import VcoAudioControls from './VcoAudioControls.web';
import usePhoneNumpadPosition from './usePhoneNumpadPosition.web';
const PhoneNumpad = ({ onCall, onHangup, hangupPending, onSendDigits, onLeaveVoicemail, callStatus, callError, connectionMessage, callDuration, canLeaveVoicemail, voicemailTargetName, initialPhoneNumber, initiallyExpanded, className, vcoEnabled, vcoRoomName }) => {
    const initialPhone = initialPhoneNumber?.trim() || '';
    const hasVco = Boolean(vcoEnabled && vcoRoomName);
    const appliedInitialPhoneRef = useRef('');
    const [isExpanded, setIsExpanded] = useState(Boolean(initialPhone && initiallyExpanded));
    const [phoneNumber, setPhoneNumber] = useState(initialPhone);
    const position = usePhoneNumpadPosition(!isExpanded && !hasVco);
    const opener = useRef(null);
    const focusAfterToggle = useRef(false);
    const toggleExpanded = (expanded) => {
        focusAfterToggle.current = true;
        setIsExpanded(expanded);
    };
    useEffect(() => {
        if (!focusAfterToggle.current)
            return;
        focusAfterToggle.current = false;
        if (isExpanded)
            position.panel.current?.querySelector('[data-dialpad-move]')?.focus();
        else
            opener.current?.focus();
    }, [isExpanded, position.panel]);
    // Add Call: patch a third/fourth hearing party into the live conference.
    const [showAddCall, setShowAddCall] = useState(false);
    const [addNumber, setAddNumber] = useState('');
    const [addStatus, setAddStatus] = useState('');
    const [addedCount, setAddedCount] = useState(0);
    const [endBusy, setEndBusy] = useState(false);
    const [endError, setEndError] = useState();
    const endInFlight = useRef(false);
    const handleEnd = useCallback(async () => {
        if (endInFlight.current)
            return;
        endInFlight.current = true;
        setEndBusy(true);
        setEndError(undefined);
        try {
            await onHangup();
        }
        catch (error) {
            setEndError(error instanceof Error ? error.message : 'Phone hangup could not be confirmed. Retry End.');
        }
        finally {
            endInFlight.current = false;
            setEndBusy(false);
        }
    }, [onHangup]);
    const handleAddParticipant = useCallback(async () => {
        const target = addNumber.trim();
        if (!target) {
            return;
        }
        setAddStatus('Dialing…');
        const ok = await twilioVoiceService.addParticipant(target);
        if (ok) {
            setAddedCount(twilioVoiceService.addedParticipantCount());
            setAddNumber('');
            setAddStatus('Added — ringing them in.');
        }
        else {
            setAddStatus('Could not add that number.');
        }
    }, [addNumber]);
    const handleDropAdded = useCallback(async () => {
        const ended = await twilioVoiceService.dropLastAddedParticipant();
        setAddedCount(twilioVoiceService.addedParticipantCount());
        setAddStatus(ended ? 'Dropped the last added line.' : 'That line could not be ended. Try dropping it again.');
    }, []);
    const isCallActive = hangupPending || callStatus === 'connected' || callStatus === 'dialing' || callStatus === 'ringing';
    const digits = [
        ['1', '2', '3'],
        ['4', '5', '6'],
        ['7', '8', '9'],
        ['*', '0', '#']
    ];
    const handleDigitPress = useCallback((digit) => {
        if (isCallActive && onSendDigits) {
            onSendDigits(digit);
            return;
        }
        setPhoneNumber(prev => prev + digit);
    }, [isCallActive, onSendDigits]);
    const handleBackspace = useCallback(() => {
        setPhoneNumber(prev => prev.slice(0, -1));
    }, []);
    const handleCall = useCallback(() => {
        if (phoneNumber.trim()) {
            // Stay expanded: collapsing here hid the only Dialing/Ringing/
            // failure feedback, so a rejected call looked like a dead button.
            onCall(phoneNumber.trim());
        }
    }, [phoneNumber, onCall]);
    // A failure with the panel closed would be invisible — surface it.
    useEffect(() => {
        if (callStatus === 'failed') {
            setIsExpanded(true);
        }
    }, [callStatus]);
    useEffect(() => {
        const nextInitialPhone = initialPhoneNumber?.trim() || '';
        if (nextInitialPhone && nextInitialPhone !== appliedInitialPhoneRef.current) {
            appliedInitialPhoneRef.current = nextInitialPhone;
            setPhoneNumber(current => current || nextInitialPhone);
            setIsExpanded(Boolean(initiallyExpanded));
        }
    }, [initialPhoneNumber, initiallyExpanded]);
    const getStatusText = () => {
        if (hangupPending)
            return endBusy ? 'Ending phone audio…' : 'Phone hangup needs confirmation';
        if (connectionMessage)
            return connectionMessage;
        switch (callStatus) {
            case 'connected':
                return 'Connected';
            case 'dialing':
                return 'Dialing...';
            case 'ringing':
                return 'Ringing...';
            case 'ended':
                return 'Call ended';
            case 'failed':
                return 'Call failed';
            default:
                return 'Ready to dial';
        }
    };
    const statusDotColor = callStatus === 'failed'
        ? '#ff6b6b'
        : callStatus === 'connected' ? '#4ade80' : '#fbbf24';
    const containerStyle = {
        position: 'absolute',
        ...position.style,
        // Above every Jitsi chrome layer (filmstrip/toolbox stack near the
        // right edge): at 1000 the collapsed toggle could sit under an
        // invisible overlay and swallow clicks — a dialer button that "does
        // nothing" reads as a broken hang-up control.
        zIndex: 10001,
        pointerEvents: 'auto',
        borderRadius: '14px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        backgroundColor: 'rgba(13, 26, 56, 0.9)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        overflowX: 'hidden',
        overflowY: 'auto',
        maxHeight: 'calc(100% - 144px)',
        maxWidth: 'calc(100% - 16px)',
        ...(isExpanded || hasVco ? { width: '280px', height: 'auto' } : { width: '60px', height: '60px' })
    };
    const toggleButtonStyle = {
        flex: 1,
        minWidth: 0,
        height: '60px',
        backgroundColor: 'transparent',
        color: '#fff',
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '24px'
    };
    const numpadButtonStyle = {
        fontSize: '20px',
        fontWeight: 'bold',
        minHeight: '45px',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        color: '#fff',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'all 0.15s ease'
    };
    const inputStyle = {
        width: '100%',
        boxSizing: 'border-box',
        padding: '12px',
        fontSize: '18px',
        textAlign: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: '8px',
        color: '#fff',
        fontFamily: 'monospace'
    };
    return (React.createElement("div", { ref: position.panel, style: containerStyle, className: className, "data-phone-dialpad": "true", onKeyDown: event => {
            if (isExpanded && event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                toggleExpanded(false);
            }
        } },
        vcoEnabled && vcoRoomName && (React.createElement("div", { style: { padding: 12 } },
            React.createElement(VcoAudioControls, { role: "interpreter", roomName: vcoRoomName }))),
        !isExpanded ? (React.createElement("div", { style: { display: 'flex', height: '60px' } },
            React.createElement("button", { style: { ...toggleButtonStyle, position: 'relative' }, ref: opener, onClick: () => toggleExpanded(true), title: callStatus === 'idle' || callStatus === 'ended' ? 'Open VRS Dialer' : getStatusText(), "aria-label": "Open dialpad", "aria-expanded": false, type: "button" },
                "\uD83D\uDCDE",
                callStatus !== 'idle' && callStatus !== 'ended' && (React.createElement("span", { style: {
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        backgroundColor: statusDotColor
                    } }))))) : (React.createElement("div", { style: { padding: '16px', color: '#fff' } },
            React.createElement("div", { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' } },
                React.createElement("button", { ...position.handle, type: "button", "data-dialpad-move": "true", style: { background: 'transparent', color: 'inherit', border: 0, minHeight: 44,
                        cursor: 'move', touchAction: 'none', fontSize: 16, fontWeight: 600 } }, "\u2194 VRS Dialer"),
                React.createElement("button", { onClick: () => toggleExpanded(false), "aria-label": "Close dialpad", type: "button", style: {
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: '#fff',
                        cursor: 'pointer',
                        fontSize: '18px',
                        minWidth: 44,
                        minHeight: 44,
                        padding: '4px'
                    } }, "\u2715")),
            React.createElement("div", { style: { marginBottom: '16px' } },
                React.createElement("input", { type: "tel", "aria-label": "Phone number", value: phoneNumber, onChange: (e) => setPhoneNumber(e.target.value), placeholder: "Enter phone number", maxLength: 20, style: inputStyle })),
            React.createElement("div", { style: {
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '8px',
                    marginBottom: '16px'
                } }, digits.flat().map(digit => (React.createElement("button", { key: digit, style: numpadButtonStyle, onClick: () => handleDigitPress(digit), disabled: hangupPending, "aria-label": isCallActive ? `Send ${digit}` : `Enter ${digit}` }, digit)))),
            React.createElement("div", { style: { display: 'flex', gap: '8px', marginBottom: '8px' } },
                React.createElement("button", { onClick: handleCall, disabled: !phoneNumber.trim() || isCallActive, style: {
                        flex: 1,
                        minHeight: '44px',
                        fontSize: '14px',
                        padding: '12px',
                        backgroundColor: colors.primary06,
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: phoneNumber.trim() && !isCallActive ? 'pointer' : 'not-allowed',
                        opacity: phoneNumber.trim() && !isCallActive ? 1 : 0.5
                    } }, "\uD83D\uDCDE Call"),
                React.createElement("button", { onClick: handleEnd, disabled: !isCallActive || endBusy, "aria-busy": endBusy, style: {
                        flex: 1,
                        minHeight: '44px',
                        fontSize: '14px',
                        padding: '12px',
                        backgroundColor: colors.error05,
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: isCallActive ? 'pointer' : 'not-allowed',
                        opacity: isCallActive ? 1 : 0.5
                    } }, endBusy ? 'Ending…' : hangupPending ? 'Retry End' : '📞 End'),
                !hangupPending && callStatus === 'connected' && twilioVoiceService.canAddParticipant() && (React.createElement("button", { onClick: () => setShowAddCall(previous => !previous), title: 'Patch another hearing party into this call', style: {
                        minWidth: '45px',
                        padding: '12px',
                        backgroundColor: 'rgba(37, 99, 235, 0.5)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer'
                    } }, "\uFF0B")),
                React.createElement("button", { onClick: handleBackspace, disabled: !phoneNumber || isCallActive, style: {
                        minWidth: '45px',
                        padding: '12px',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: phoneNumber && !isCallActive ? 'pointer' : 'not-allowed',
                        opacity: phoneNumber && !isCallActive ? 1 : 0.5
                    } }, "\u232B")),
            showAddCall && !hangupPending && callStatus === 'connected' && (React.createElement("div", { style: { marginBottom: '8px' } },
                React.createElement("div", { style: { display: 'flex', gap: '6px', marginBottom: '6px' } },
                    React.createElement("input", { type: "tel", value: addNumber, onChange: (e) => setAddNumber(e.target.value), placeholder: "Add hearing party\u2026", maxLength: 20, style: { ...inputStyle, textAlign: 'left', fontSize: '14px', padding: '9px' } }),
                    React.createElement("button", { onClick: handleAddParticipant, disabled: !addNumber.trim(), style: {
                            padding: '9px 12px',
                            backgroundColor: '#2563eb',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: addNumber.trim() ? 'pointer' : 'not-allowed',
                            opacity: addNumber.trim() ? 1 : 0.5,
                            fontWeight: 700
                        } }, "Dial In")),
                (addStatus || addedCount > 0) && (React.createElement("div", { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px', opacity: 0.8 } },
                    React.createElement("span", null,
                        addStatus,
                        addedCount > 0 ? ` ${addedCount} added line${addedCount === 1 ? '' : 's'}.` : ''),
                    addedCount > 0 && (React.createElement("button", { onClick: handleDropAdded, style: { background: 'none', border: 'none', color: '#ff8a8a', cursor: 'pointer', fontSize: '11px', fontWeight: 700 } }, "Drop last")))))),
            onLeaveVoicemail && (React.createElement("button", { onClick: onLeaveVoicemail, disabled: !canLeaveVoicemail || hangupPending, style: {
                    width: '100%',
                    padding: '12px',
                    marginBottom: '8px',
                    backgroundColor: 'rgba(21, 101, 192, 0.9)',
                    color: '#fff',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                    cursor: canLeaveVoicemail ? 'pointer' : 'not-allowed',
                    opacity: canLeaveVoicemail ? 1 : 0.5,
                    fontWeight: 700
                }, title: canLeaveVoicemail
                    ? `Leave VideoMail${voicemailTargetName ? ` for ${voicemailTargetName}` : ''}`
                    : 'VideoMail requires a known client in this VRS session', type: "button" }, "Leave VideoMail")),
            React.createElement("div", { style: {
                    padding: '8px',
                    backgroundColor: callStatus === 'failed' ? 'rgba(180, 35, 24, 0.35)' : 'rgba(0, 0, 0, 0.3)',
                    borderRadius: '8px',
                    fontSize: '14px'
                } },
                React.createElement("div", { style: { display: 'flex', alignItems: 'center', justifyContent: 'center' } },
                    callStatus !== 'idle' && callStatus !== 'ended' && (React.createElement("span", { style: { marginRight: '8px', color: statusDotColor } }, "\u25CF")),
                    React.createElement("span", { role: "status", "aria-live": "polite" }, getStatusText()),
                    callStatus === 'connected' && !connectionMessage && !hangupPending && callDuration && (React.createElement("span", { role: "timer", "aria-live": "off", style: { marginLeft: '4px' } }, callDuration))),
                (callError || endError) && (React.createElement("div", { role: "alert", style: {
                        marginTop: '6px',
                        fontSize: '14px',
                        lineHeight: 1.4,
                        textAlign: 'center',
                        color: '#ffd7d3'
                    } }, callError || endError)))))));
};
export default PhoneNumpad;
